/* eslint-disable @typescript-eslint/naming-convention */
import { Inject, Injectable } from '@angular/core';
import { IPixabayImages } from '@boxx/boxx-images/lib/contracts/IImages';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CategoryModel } from '../models/Category.model';
import { EcwidModel } from '../models/Ecwid.model';
import { EcwidImageModel } from '../models/EcwidImage.model';
import { ImageModel } from '../models/Image.model';
import { OrderModel } from '../models/Order.model';
import { IProductImages, IProductImageUpload, ProductModel } from '../models/Product.model';
import { IUpdateProfileStorePayload, StatesModelProps, StoreProfileModel } from '../models/Store.model';
import { VariationModel } from '../models/Variation.model';
import {
    ICategoryImageUploadApiModel,
    ICreateOrderPayload,
    ICreateVariationPayload,
    IEcwidImageUpload,
    IProductApiModel,
    IProfileApiModel,
    IUpdateStatusOrderPayload,
    IUpsertCategoryPayload,
    IUpsertProductPayload
} from '../repositories/IStore.api';
import { IStoreRepository, STORE_REPOSITORY } from '../repositories/IStore.repository';
import { IStoreService } from './IStore.service';

@Injectable({
    providedIn: 'root'
})
export class StoreService implements IStoreService {
    constructor(
        @Inject(STORE_REPOSITORY) private repository: IStoreRepository
    ) { }

    // --------------------- CATEGORIES: -------------------------
    loadCategories(): Observable<CategoryModel[]> {
        return this.repository.loadCategories().pipe(
            map(response => response.data.attributes.items.map(c => CategoryModel.fromApi(c))),
            catchError(error => { throw error; })
        );
    }
    loadCategoryById(id: number): Observable<CategoryModel> {
        return this.repository.loadCategoryById(id).pipe(
            map(response => CategoryModel.fromApi(response.data)),
            catchError(error => { throw error; })
        );
    }
    getSingleCategory(id: number): Observable<ICategoryImageUploadApiModel> {
        return this.repository.getSingleCategory(id).pipe(
            map(response => CategoryModel.fromApiUpload(response.data.attributes)),
            catchError(error => { throw error; })
        );
    }
    
    createCategory(payload: IUpsertCategoryPayload): Observable<CategoryModel> {
        return this.repository.createCategory(payload).pipe(
            map( (response) => CategoryModel.fromApi(response.data)),
            catchError(error => { throw error; })
        );
    }

    // createCategory(payload: IUpsertCategoryPayload): Promise<any> {
    //     return this.repository
    //       .createCategory(payload)
    //       .then((res) => {
    //         return res;
    //       });
    //   }
    
    updateCategory(payload: IUpsertCategoryPayload): Observable<CategoryModel> {
        return this.repository.updateCategory(payload).pipe(
            map(response => CategoryModel.fromApi(response.data)),
            catchError(error => { throw error; })
        );
    }
    deleteCategory(payload: any): Observable<boolean> {
        return this.repository.deleteCategory(payload).pipe(
            map(resp => resp.status === 'success'),
            catchError(error => { throw error; })
        );
    }
    uploadImageCategory(id: number, file: string): Observable<EcwidImageModel> {
        return this.repository.uploadImageCategory(id, file).pipe(
            map(response => EcwidImageModel.fromApi(response.data)),
            catchError(error => { throw error; })
        );
    }
    deleteImageCategory(payload: any): Observable<boolean> {
        return this.repository.deleteImageCategory(payload).pipe(
            map(resp => resp.status === 'success'),
            catchError(error => { throw error; })
        );
    }

    // --------------------- ORDERS: -------------------------
    loadOrders(): Observable<OrderModel[]> {
        return this.repository.loadOrders().pipe(
            map(response => response.data.attributes.items.map(o => OrderModel.fromApi(o))),
            catchError(error => { throw error; })
        );
    }
    loadOrderById(id: string): Observable<OrderModel> {
        return this.repository.loadOrderById(id).pipe(
            map(response => OrderModel.fromApi(response.data)),
            catchError(error => { throw error; })
        );
    }
    updateOrder(payload: IUpdateStatusOrderPayload): Observable<OrderModel> {
        return this.repository.updateOrder(payload).pipe(
            map(response => OrderModel.fromApi(response.data)),
            catchError(error => { throw error; })
        );
    }
    createOrder(payload: ICreateOrderPayload): Observable<OrderModel> {
        return this.repository.createOrder(payload).pipe(
            map(response => OrderModel.fromApi(response.data)),
            catchError(error => { throw error; })
        );
    }
    deleteOrder(id: string): Observable<boolean> {
        return this.repository.deleteOrder(id).pipe(
            map(resp => resp.status === 'success'),
            catchError(error => { throw error; })
        );
    }
    
    // --------------------- PRODUCTS: -------------------------
    loadProducts(): Observable<ProductModel[]> {
        return this.repository.loadProducts().pipe(
            map(response => response.data.attributes.items.map(p => ProductModel.fromApi(p))),
            catchError(error => { throw error; })
        );
    }
    loadProductById(id: number): Observable<ProductModel> {
        return this.repository.loadProductById(id).pipe(
            map(response => ProductModel.fromApi(response.data)),
            catchError(error => { throw error; })
        );
    }
    createProduct(payload: IUpsertProductPayload): Observable<ProductModel> {
        return this.repository.createProduct(payload).pipe(
            map(response => ProductModel.fromApi(response.data)),
            catchError(error => { throw error; })
        );
    }
    updateProduct(payload: IUpsertProductPayload): Observable<ProductModel> {
        return this.repository.updateProduct(payload).pipe(
            map(response => ProductModel.fromApi(response.data)),
            catchError(error => { throw error; })
        );
    }
    deleteProduct(id: number): Observable<boolean> {
        return this.repository.deleteProduct(id).pipe(
            map(resp => resp.status === 'success'),
            catchError(error => { throw error; })
        );
    }
    getSingleProduct(id: number): Observable<IProductApiModel> {
        return this.repository.getSingleProduct(id).pipe(
            map(response => {
                return ProductModel.fromApiSingle(response.data.attributes, id);
            }),
            catchError(error => { throw error; })
        );
    }
    uploadImageProduct(payload: IProductImageUpload): Promise<any> {
        return this.repository
          .uploadImageProduct(payload)
          .pipe(map((response) => response))
          .toPromise();
      }
    deleteImageProduct(payload: any): Observable<boolean> {
        return this.repository.deleteImageProduct(payload).pipe(
            map(resp => resp.status === 'success'),
            catchError(error => { throw error; })
        );
    }

    // ---------------- PRODUCT VARIATIONS: ---------------------
    loadProductsVariations(): Observable<VariationModel[]> {
        return this.repository.loadProductsVariations().pipe(
            map(resp => resp.data.map(v => VariationModel.fromApi(v))),
            catchError(error => { throw error; })
        );
    }
    loadProductVariationById(id: number): Observable<VariationModel> {
        return this.repository.loadProductVariationByVariationId(id).pipe(
            map(resp => VariationModel.fromApi(resp.data)),
            catchError(error => { throw error; })
        );
    }
    createProductVariation(payload: ICreateVariationPayload): Observable<VariationModel> {
        return this.repository.createProductVariation(payload).pipe(
            map(resp => VariationModel.fromApi(resp.data)),
            catchError(error => { throw error; })
        );
    }
    deleteProductVariation(id: number): Observable<boolean> {
        return this.repository.deleteProductVariation(id).pipe(
            map(resp => resp.status === 'success'),
            catchError(error => { throw error; })
        );
    }

    // --------------------- IMAGES: -------------------------
    loadSiteImages(): Observable<ImageModel[]> {
        return this.repository.loadSiteImages().pipe(
            map(resp => resp.data.map(i => ImageModel.fromApi(i))),
            catchError(error => { throw error; })
        );
    }
    loadSiteImageById(id: number): Observable<ImageModel> {
        throw new Error('Method not implemented');
    }
    createSiteImage(payload: number): Observable<ImageModel> {
        throw new Error('Method not implemented');
    }
    deleteSiteImage(id: number): Observable<boolean> {
        throw new Error('Method not implemented');
    }

    searchImages(imageFilter: any): any {
        return this.repository.searchImages(imageFilter).pipe(
            map((response) => response),
            catchError(error => { throw error; })
        );
    }

    // ---------------- ECWID: ---------------------
    getLinkValidation(): Observable<EcwidModel> {
        return this.repository.getLinkValidation().pipe(
            map(response => response.data),
            catchError(error => { throw error; })
        );
    }

    getLinkSSO(): Observable<EcwidModel> {
        return this.repository.getLinkSSO().pipe(
            map(response => response.data),
            catchError(error => { throw error; })
        );
    }   
    
    openSSOHidden(url: string): Observable<EcwidModel> {
        return this.repository.openSSOHidden(url).pipe(
            map(response => response.data),
            catchError(error => { throw error; })
        );
    }    

    getToken(bId: number, businessId: number): Observable<EcwidModel> {
        return this.repository.getToken(bId,businessId).pipe(
            map(response => response.data),
            catchError(error => { throw error; })
        );
    }

    getProfile(): Observable<StoreProfileModel> {
        return this.repository.getProfile().pipe(
            map(response => StoreProfileModel.fromApi(response.data.attributes)),
            catchError(error => { throw error; })
        );
    }

    updateProfile(payload: IUpdateProfileStorePayload): Observable<IProfileApiModel> {
        return this.repository.updateProfile(payload).pipe(
            map(response => response.data),
            catchError(error => { throw error; })
        );
    }

    uploadImageProfile(file: string): Observable<EcwidImageModel> {
        return this.repository.uploadImageProfile(file).pipe(
            map(response => EcwidImageModel.fromApi(response.data)),
            catchError(error => { throw error; })
        );
    }

    deleteImageProfile(payload: any): Observable<boolean> {
        return this.repository.deleteImageProfile(payload).pipe(
            map(resp => resp.status === 'success'),
            catchError(error => { throw error; })
        );
    }



    // --------------------- PRODUCTS: -------------------------
    loadCoupons(): Observable<any[]> {
        return this.repository.loadCoupons().pipe(
            map(response => {
                if(response.data.attributes.items !== undefined){
                    return response.data.attributes.items.map(p => p)
                }
              else{
                return []
              }
            }),
            catchError(error => { throw error; })
        );
    }
    createCoupon(payload: any): Observable<any> {
        return this.repository.createCoupon(payload).pipe(
            map(response => response.data),
            catchError(error => { throw error; })
        );
    }
    updateCoupon(payload: any): Observable<any> {
        return this.repository.updateCoupon(payload).pipe(
            map(response => response.data),
            catchError(error => { throw error; })
        );
    }
    deleteCoupon(id: number): Observable<boolean> {
        return this.repository.deleteCoupon(id).pipe(
            map(resp => resp.status === 'success'),
            catchError(error => { throw error; })
        );
    }
    getSingleCoupon(id: number): Observable<any> {
        return this.repository.getSingleCoupon(id).pipe(
            map(response => {
                return response.data.attributes;
            }),
            catchError(error => { throw error; })
        );
    }
}